import { z } from 'zod';

import { BankFeedUserPermissionsSchema } from '@/domain/bankFeed/schemas';

export const RoleSchema = z.enum(['accountant', 'user', 'confidential-user', 'supplier']);

export type Role = z.infer<typeof RoleSchema>;

// The role block one company overrides. Values come back normalised, and
// `displayRole` is a translated label, so it is not an enum.
export const CompanyPermissionsSchema = z.object({
  role: RoleSchema,
  restricted: z.boolean(),
  limited: z.boolean(),
  readonly: z.boolean(),
  displayRole: z.string(),
});

export type CompanyPermissions = z.infer<typeof CompanyPermissionsSchema>;

export type RoleModificators = Pick<CompanyPermissions, 'restricted' | 'limited' | 'readonly'>;

// Keyed by company id, holding only the companies that carry an override.
// Always empty for an administrator or a support user.
export const CompanyPermissionsMapSchema = z.record(z.string(), CompanyPermissionsSchema);

const ApProfileSchema = z.object({
  userGUID: z.string(),
  userID: z.string(),
  // The organization values; a company override lives in `companyPermissions`.
  role: RoleSchema,
  title: z.string().nullable(),
  isSuperUser: z.boolean(),
  // Optional until every backend returns it; the reducer reads a missing one as false.
  isOrgAdmin: z.boolean().optional(),
  chatToken: z.string(),
  picture: z.string().nullable(),
  phone: z.string().nullable(),
  displayName: z.string(),
  organizationId: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  showMainTour: z.boolean(),
  // Both optional while the legacy flag is still accepted: see the fallback in the
  // env reducer. A required field would fail the whole profile on a backend that
  // has not shipped `isRecordingEnabled` yet.
  isRecordingEnabled: z.boolean().optional(),
  isFullstoryRecording: z.boolean().optional(),
  isDokkaSupport: z.boolean(),
  permissions: z.object({
    notes: z.object({ create: z.boolean(), update: z.boolean(), delete: z.boolean() }),
  }),
  // Stored per user as an open key set merged over defaults — only the keys
  // we read are declared.
  features: z.looseObject({
    modal: z.boolean(),
    filter_documents_tagged_by_user_only: z.boolean(),
  }),
  restricted: z.boolean(),
  limited: z.boolean(),
  readonly: z.boolean(),
  cognitoUsername: z.string().nullable(),
  support_portal: z.string().nullable(),
  organizationName: z.string(),
  organizationBot: z.object({
    userID: z.string(),
    displayName: z.string(),
    picture: z.string().nullable(),
  }),
  newChatFlow: z.boolean(),
  allowFreshdesk: z.boolean(),
  beta: z.boolean(),
  // TEMPORARY: absent until the backend ships the per-company override;
  // drop the default once it does.
  companyPermissions: CompanyPermissionsMapSchema.default({}),
});

// Null when the user has no MC app row.
const McProfileSchema = z.object({
  userID: z.string(),
  email: z.string(),
  name: z.string(),
  chatToken: z.string(),
  // The MC app row stores its own role, not validated against the base roles.
  role: z.string().nullable(),
  title: z.string().nullable(),
  isAdmin: z.boolean(),
});

const appEntrySchema = <P extends z.ZodType>(profile: P) =>
  z.object({
    appName: z.string(),
    appUrl: z.string().nullable(),
    active: z.boolean(),
    profile,
    companiesContext: z.array(z.string()),
  });

export const UserProfileResponseSchema = z.object({
  AP: appEntrySchema(ApProfileSchema),
  MC: appEntrySchema(McProfileSchema.nullable()),
  // The PAY entry never carries a profile.
  PAY: appEntrySchema(z.null()),
});

export type UserProfileResponse = z.infer<typeof UserProfileResponseSchema>;

// What the signed-in user may do in the company in scope.
export const CompanyUserPreferencesResponseSchema = z.object({
  bank_feed_permissions: BankFeedUserPermissionsSchema,
});
